// Zwischenstand des Firmen-Bestellformulars im Browser (localStorage). Er
// übersteht den Umweg über den Karten-Designer und ein Neuladen; serverseitige
// Bestellentwürfe gibt es bewusst nicht. Reines JS ohne React, damit die
// Backend-Suite es testen kann.
//
// Gespeichert werden nur IDs (Produkt, Design, zugewiesene Mitglieder) und
// öffentliche Upload-URLs — keine Namen oder E-Mails von Mitarbeitenden.

const VERSION = 1;
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
// Rest-Gültigkeit, unter der ein Bild-Upload verworfen wird: das Absenden
// (inkl. Designer-Umweg) soll nicht an einem eben abgelaufenen Entwurf scheitern.
const DRAFT_MARGIN_MS = 5 * 60 * 1000;

export const STORAGE_PREFIX = 'sv:company-order:';

export const storageKey = (companyId, userId) =>
  `${STORAGE_PREFIX}v${VERSION}:${companyId}:${userId}`;

const randomId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const newItem = (overrides = {}) => ({
  key: randomId(),
  productId: '',
  amount: 1,
  source: 'upload',
  // Signierter Entwurf aus /orders/customize und die Vorschau-URLs der
  // hochgeladenen Seiten (null = Seite nicht verwendet).
  cardDraft: null,
  uploads: { front: null, back: null },
  designId: null,
  assignments: [null],
  // Sonderpreis pro Karte in CHF, nur im Formular des Plattform-Admins;
  // leer = Listenpreis des Produkts.
  customPrice: '',
  ...overrides,
});

// formId ist zugleich die clientRequestId: ein erneutes Absenden desselben
// Formulars findet serverseitig die bestehende Bestellung.
export const createEmptyForm = () => ({
  version: VERSION,
  formId: randomId(),
  items: [newItem()],
  shippingFee: 500,
  shippingAddress: null,
});

export const load = (storage, key, now = Date.now()) => {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (stored?.version !== VERSION || !(now - stored.savedAt < TTL_MS)) {
      storage.removeItem(key);
      return null;
    }
    const form = { ...stored };
    delete form.savedAt;
    return form;
  } catch {
    return null;
  }
};

export const save = (storage, key, form, now = Date.now()) => {
  try {
    storage.setItem(key, JSON.stringify({ ...form, savedAt: now }));
  } catch {
    // Voller oder gesperrter Speicher: das Formular funktioniert weiter,
    // überlebt dann nur keinen Seitenwechsel.
  }
};

export const clear = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch {
    // siehe save
  }
};

// Beim Logout: alle Formularstände dieses Browsers entfernen.
export const clearAllOrderForms = (storage) => {
  try {
    const keys = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // siehe save
  }
};

/** Eine Zuweisung pro Karte: kürzt oder füllt mit Reserve (null) auf. */
export const resizeAssignments = (assignments, amount) =>
  Array.from({ length: amount }, (_, i) => assignments[i] ?? null);

/**
 * Rücksprung aus dem Designer (?item=K&designId=D): das Design an Position K
 * hängen. Gibt es K nicht (mehr) oder fehlt `item`, kommt eine neue Position
 * dazu — `matched` sagt dem Aufrufer, ob er darauf hinweisen soll.
 */
export const applyReturnedDesign = (form, { itemKey, designId, productId }) => {
  const index = itemKey
    ? form.items.findIndex((item) => item.key === itemKey)
    : -1;
  if (index >= 0) {
    const items = form.items.map((item, i) =>
      i === index
        ? {
            ...item,
            source: 'design',
            designId,
            cardDraft: null,
            uploads: { front: null, back: null },
          }
        : item
    );
    return { form: { ...form, items }, matched: true };
  }
  const item = newItem({
    source: 'design',
    designId,
    productId: productId ?? '',
  });
  // Eine leere Startposition ersetzen statt eine zweite anzuhängen.
  const items =
    form.items.length === 1 && isBlank(form.items[0])
      ? [item]
      : [...form.items, item];
  return { form: { ...form, items }, matched: !itemKey };
};

const isBlank = (item) =>
  !item.productId && !item.cardDraft && !item.designId;

/** Kopie einer Position — ohne den Upload-Entwurf (ein Upload = eine Karte). */
export const duplicateItem = (item) =>
  newItem({
    productId: item.productId,
    amount: item.amount,
    source: item.source,
    designId: item.designId,
    assignments: resizeAssignments([], item.amount),
    customPrice: item.customPrice ?? '',
  });

/**
 * Sonderpreis einer Position in Rappen, `null` ohne Eingabe (dann gilt der
 * Listenpreis) und `NaN` bei ungültiger Eingabe (keine Zahl oder negativ).
 * Ältere Zwischenstände kennen das Feld nicht.
 */
export const customUnitPrice = (item) => {
  const raw = String(item.customPrice ?? '').trim().replace(',', '.');
  if (raw === '') return null;
  const chf = Number(raw);
  if (!Number.isFinite(chf) || chf < 0) return Number.NaN;
  return Math.round(chf * 100);
};

/**
 * true, wenn der signierte Entwurf abgelaufen ist oder in weniger als fünf
 * Minuten abläuft. Liest `exp` ungeprüft aus dem JWT — nur ein UX-Hinweis,
 * geprüft wird serverseitig.
 */
export const isDraftExpiring = (cardDraft, now = Date.now()) => {
  try {
    const payload = cardDraft.split('.')[1];
    const json = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );
    return !(json.exp * 1000 - now > DRAFT_MARGIN_MS);
  } catch {
    return true;
  }
};
